
export interface AmortizationPayment {
  paymentNumber: number;
  paymentAmount: number;
  principalPayment: number;
  interestPayment: number;
  remainingBalance: number;
  cumulativeInterest: number;
  cumulativePrincipal: number;
}

export interface AmortizationSchedule {
  monthlyPayments: AmortizationPayment[];
  yearlyPayments: AmortizationPayment[];
  totalInterest: number;
  totalPrincipal: number;
}

/**
 * Calculate detailed amortization schedule for a loan
 */
export const calculateAmortizationSchedule = (
  loanAmount: number,
  annualInterestRate: number,
  loanTermYears: number
): AmortizationSchedule => {
  const monthlyInterestRate = annualInterestRate / 100 / 12;
  const numberOfPayments = loanTermYears * 12;
  
  // Calculate monthly payment using the standard mortgage formula
  let monthlyPayment: number;
  if (monthlyInterestRate === 0) {
    monthlyPayment = loanAmount / numberOfPayments;
  } else {
    monthlyPayment = loanAmount * 
      (monthlyInterestRate * Math.pow(1 + monthlyInterestRate, numberOfPayments)) / 
      (Math.pow(1 + monthlyInterestRate, numberOfPayments) - 1);
  }

  const monthlyPayments: AmortizationPayment[] = [];
  let remainingBalance = loanAmount;
  let cumulativeInterest = 0;
  let cumulativePrincipal = 0;

  // Calculate each monthly payment
  for (let month = 1; month <= numberOfPayments; month++) {
    const interestPayment = remainingBalance * monthlyInterestRate;
    const principalPayment = monthlyPayment - interestPayment;
    
    remainingBalance = Math.max(0, remainingBalance - principalPayment);
    cumulativeInterest += interestPayment;
    cumulativePrincipal += principalPayment;

    monthlyPayments.push({
      paymentNumber: month,
      paymentAmount: monthlyPayment,
      principalPayment,
      interestPayment,
      remainingBalance,
      cumulativeInterest,
      cumulativePrincipal
    });
  }

  // Calculate yearly summaries
  const yearlyPayments: AmortizationPayment[] = [];
  for (let year = 1; year <= loanTermYears; year++) {
    const endMonth = year * 12;
    const yearData = monthlyPayments[endMonth - 1];
    
    const startMonth = (year - 1) * 12;
    const yearlyPrincipal = monthlyPayments
      .slice(startMonth, endMonth)
      .reduce((sum, payment) => sum + payment.principalPayment, 0);
    const yearlyInterest = monthlyPayments
      .slice(startMonth, endMonth)
      .reduce((sum, payment) => sum + payment.interestPayment, 0);

    yearlyPayments.push({
      paymentNumber: year,
      paymentAmount: yearlyPrincipal + yearlyInterest,
      principalPayment: yearlyPrincipal,
      interestPayment: yearlyInterest,
      remainingBalance: yearData.remainingBalance,
      cumulativeInterest: yearData.cumulativeInterest,
      cumulativePrincipal: yearData.cumulativePrincipal
    });
  }

  return {
    monthlyPayments,
    yearlyPayments,
    totalInterest: cumulativeInterest,
    totalPrincipal: loanAmount
  };
};
